/**
 * web-client-app D4 / D11 — the frame of every website page: the studio's
 * header, the page in <main>, the footer.
 *
 * Scrolling, as the prototype does it: a new page opens at its top; an address
 * with a #section scrolls to that section once it has drawn (the home's
 * sections wait for their reads), smoothly when already on that page; Back and
 * Forward return to where the page was scrolled.
 *
 * web-studio-sites D3: on a studio's custom site the server puts the site's own
 * header and footer around the page, so the frame is the page alone at every
 * width: no Telify header or footer, no app screens. An address the server
 * answered with 404 draws the not-found page.
 */
import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigationType } from 'react-router-dom';
import { matchWebPage } from '@telify/shared/browser';

import { ToastProvider } from '@/components/Toast/ToastContext';

import { bindSessionEnd } from '../api/session';
import { readBoot } from '../boot';
import { KitPlacements, useSessionPageEvents } from '../kit/runtime';
import { MessagesPanelProvider } from '../messages/panelContext';
import { WebChatProvider } from '../messages/WebChat';
import { MessageBanner } from '../pieces/message-banner';
import { NotFoundPage } from '../pages/NotFoundPage';
import { MessagesPanel } from '../pieces/messages-panel';
import { PhoneMessages } from '../phone/PhoneMessages';
import { StudioFooter } from '../pieces/studio-footer';
import { StudioHeader } from '../pieces/studio-header';
import { readSite } from '../site';
import { useAppScreens } from '../ui/useIsPhone';
import css from './WebLayout.module.css';

/** How long an address waits for its section, or a page for its height, before giving up. */
const WAIT_MS = 4000;
/** After landing on a section, how long it is held in place while the sections above it finish loading. */
const SETTLE_MS = 1500;
const VISITOR_SCROLLS = ['wheel', 'touchstart', 'keydown'] as const;

/** Calls `step` every frame until it returns true, the time runs out or the visitor scrolls. Returns a stop. */
function everyFrame(step: (now: number) => boolean, ms: number, onTimeout?: () => void): () => void {
  const until = performance.now() + ms;
  let frame = 0;
  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    cancelAnimationFrame(frame);
    for (const type of VISITOR_SCROLLS) window.removeEventListener(type, stop);
  };
  for (const type of VISITOR_SCROLLS) window.addEventListener(type, stop, { passive: true });
  const tick = () => {
    if (stopped) return;
    const now = performance.now();
    if (step(now)) return stop();
    if (now > until) {
      stop();
      onTimeout?.();
      return;
    }
    frame = requestAnimationFrame(tick);
  };
  tick();
  return stop;
}

function scrollToSection(id: string, behavior: ScrollBehavior): () => void {
  const pad = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
  let settleUntil = 0;
  return everyFrame((now) => {
    const el = document.getElementById(id);
    if (!el) return false;
    if (!settleUntil) {
      el.scrollIntoView({ behavior, block: 'start' });
      if (behavior === 'smooth') return true;
      settleUntil = now + SETTLE_MS;
    } else if (Math.abs(el.getBoundingClientRect().top - pad) > 2) {
      el.scrollIntoView({ block: 'start' });
    }
    return now > settleUntil;
  }, WAIT_MS + SETTLE_MS);
}

function useWebScroll() {
  const location = useLocation();
  const navType = useNavigationType();
  const lastPath = useRef<string | null>(null);
  const saved = useRef(new Map<string, number>());

  useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  }, []);

  // Remember where this page is scrolled, for Back and Forward. A layout effect, so the
  // listener is gone before the next page draws and its height change is never recorded here.
  useLayoutEffect(() => {
    const key = location.key;
    const save = () => saved.current.set(key, window.scrollY);
    window.addEventListener('scroll', save, { passive: true });
    return () => window.removeEventListener('scroll', save);
  }, [location.key]);

  useLayoutEffect(() => {
    const first = lastPath.current === null;
    const samePage = lastPath.current === location.pathname;
    lastPath.current = location.pathname;

    if (navType === 'POP' && !first) {
      const y = saved.current.get(location.key) ?? 0;
      const go = () => window.scrollTo(0, y);
      return everyFrame(
        () => {
          if (document.documentElement.scrollHeight - window.innerHeight < y) return false;
          go();
          return true;
        },
        WAIT_MS,
        go,
      );
    }

    const id = decodeURIComponent(location.hash.slice(1));
    const behavior: ScrollBehavior = samePage ? 'smooth' : 'auto';
    if (id) return scrollToSection(id, behavior);
    if (!first) window.scrollTo({ top: 0, behavior });
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per navigation
  }, [location.key]);
}

const PHONE_SCREENS = new Set([
  'home', 'about', 'invite', 'waitlists', 'service', 'event', 'series', 'services', 'events', 'checkout', 'booked', 'pay', 'form-request', 'bookings', 'booking', 'complete', 'profile',
]);

/**
 * A custom site's frame: the page alone, between the site's own header and footer, which the server wrote.
 * The page is Telify's content, never her shell, so it carries `data-telify-screen`: base.css gives it the
 * baseline, body font and text color the standard website's page-wide rules give Telify's pages there
 * (web-studio-sites D4), including the page markup that sits outside any piece (a page's own title).
 */
function CustomSiteFrame({ children }: { children: ReactNode }) {
  // web-studio-sites D4: placeholders in her header and footer fill on Telify's pages too, and the page hears sign-in and sign-out.
  useSessionPageEvents();
  return (
    <>
      <main data-telify-screen="">{children}</main>
      <KitPlacements />
      <MessagesPanel />
      <MessageBanner />
    </>
  );
}

export function WebLayout() {
  useWebScroll();
  // web-sign-in-checkout D5: a signed-in request answering 401 signs the visitor out here.
  const queryClient = useQueryClient();
  useEffect(() => bindSessionEnd(queryClient), [queryClient]);
  const { pathname } = useLocation();
  const customSite = readSite() !== null;
  // The address this page was loaded at, which the server's boot block answered for.
  const loadedAt = useRef(pathname);
  const serverNotFound = customSite && readBoot()?.notFound === true && pathname === loadedAt.current;
  // The phone home, About, Invite and Waitlists, the Book screen, service, event and series pages, the checkout, the booked
  // and pay pages, a form, and the signed-in client's Bookings, booking and Profile pages are whole screens of their
  // own, as in the app: no header or footer. On them the messages are the app's chat screen (web-account-pages D8), and the app's toasts show.
  const page = matchWebPage(pathname)?.page;
  const phoneScreen = useAppScreens() && page !== undefined && PHONE_SCREENS.has(page);
  // web-account-pages D3: one panel state and one live chat per tab, across the phone and computer layouts.
  return (
    <MessagesPanelProvider>
      <WebChatProvider>
        {customSite ? (
          <CustomSiteFrame>{serverNotFound ? <NotFoundPage /> : <Outlet />}</CustomSiteFrame>
        ) : phoneScreen ? (
          <ToastProvider>
            <div className={css.phone}>
              <Outlet />
            </div>
            <PhoneMessages />
          </ToastProvider>
        ) : (
          <>
            <div className={css.site}>
              <StudioHeader />
              <main className={css.main}>
                <Outlet />
              </main>
              <StudioFooter />
            </div>
            <MessagesPanel />
            <MessageBanner />
          </>
        )}
      </WebChatProvider>
    </MessagesPanelProvider>
  );
}
